import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';
import { invoiceService } from '../services/invoiceService';
import { customerService } from '../services/customerService';

const SERVICE_ITEMS = [
  'Deposit',
  'Deposit for construction',
  'Progress payment 1',
  'Partial payment 1',
  'Progress payment 2',
  'Change Order',
  'Final Payment',
  'Architectural 3D Design'
];

const PAYMENT_TERMS = [
  'Due on Receipt',
  'Net 15',
  'Net 30',
  'Net 45',
  'Net 60'
];

export default function InvoiceCreate() {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { toast } = useToast();
  const isEdit = Boolean(invoiceId);

  // Form state
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const getTermsForPaymentTerms = (paymentTerm) => {
    const termsMap = {
      'Due on Receipt': 'Payment is due immediately upon receipt of invoice. Please make all checks payable to Reliable Landscaping and Design. Late payments may be subject to additional fees. Thank you for your business!',
      'Net 15': 'Payment is due within 15 days of invoice date. Please make all checks payable to Reliable Landscaping and Design. Late payments may be subject to additional fees. Thank you for your business!',
      'Net 30': 'Payment is due within 30 days of invoice date. Please make all checks payable to Reliable Landscaping and Design. Late payments may be subject to additional fees. Thank you for your business!',
      'Net 45': 'Payment is due within 45 days of invoice date. Please make all checks payable to Reliable Landscaping and Design. Late payments may be subject to additional fees. Thank you for your business!',
      'Net 60': 'Payment is due within 60 days of invoice date. Please make all checks payable to Reliable Landscaping and Design. Late payments may be subject to additional fees. Thank you for your business!'
    };
    return termsMap[paymentTerm] || termsMap['Net 30'];
  };

  const [formData, setFormData] = useState({
    customer_id: '',
    customer_first_name: '',
    customer_last_name: '',
    customer_address: '',
    customer_phone: '',
    customer_email: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    line_items: [{ description: '', quantity: 1, price: 0, total: 0 }],
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    discount: 0,
    total: 0,
    payment_terms: 'Net 30',
    status: 'draft',
    notes: '',
    terms_conditions: 'Payment is due within 30 days of invoice date. Please make all checks payable to Reliable Landscaping and Design. Late payments may be subject to additional fees. Thank you for your business!'
  });

  useEffect(() => {
    if (isEdit) {
      fetchInvoice();
    } else {
      // Set default due date to 30 days from now
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30);
      setFormData(prev => ({ ...prev, due_date: dueDate.toISOString().split('T')[0] }));
    }
  }, [invoiceId]);

  // Server-side customer search (debounced) — the backend caps pages at 100,
  // so filtering must happen in the query, not over a single fetched page
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const params = { page: 1, limit: 100 };
        if (customerSearch.trim()) params.search = customerSearch.trim();
        const data = await customerService.getAll(params);
        setCustomers(data.data || []);
      } catch (error) {
        console.error('Error fetching customers:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load customers. Please refresh the page."
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const invoice = await invoiceService.getById(invoiceId);
      setFormData({
        ...invoice,
        invoice_date: invoice.invoice_date.split('T')[0],
        due_date: invoice.due_date.split('T')[0]
      });
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load invoice"
      });
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setFormData(prev => ({
        ...prev,
        customer_id: customer.id,
        customer_first_name: customer.first_name || customer.name?.split(' ')[0] || '',
        customer_last_name: customer.last_name || customer.name?.split(' ')[1] || '',
        customer_address: customer.address || '',
        customer_phone: customer.phone || '',
        customer_email: customer.email || ''
      }));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index][field] = value;
    
    // Calculate total for this line item
    if (field === 'quantity' || field === 'price') {
      const quantity = parseFloat(newLineItems[index].quantity) || 0;
      const price = parseFloat(newLineItems[index].price) || 0;
      newLineItems[index].total = quantity * price;
    }
    
    setFormData(prev => ({ ...prev, line_items: newLineItems }));
    calculateTotals(newLineItems, formData.tax_rate, formData.discount);
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, price: 0, total: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    const newLineItems = formData.line_items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, line_items: newLineItems }));
    calculateTotals(newLineItems, formData.tax_rate, formData.discount);
  };

  const calculateTotals = (lineItems, taxRate, discount) => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = (subtotal * (parseFloat(taxRate) || 0)) / 100;
    const total = subtotal + taxAmount - (parseFloat(discount) || 0);
    
    setFormData(prev => ({
      ...prev,
      subtotal: subtotal,
      tax_amount: taxAmount,
      total: Math.max(0, total)
    }));
  };

  const handleTaxRateChange = (value) => {
    setFormData(prev => ({ ...prev, tax_rate: parseFloat(value) || 0 }));
    calculateTotals(formData.line_items, value, formData.discount);
  };

  const handleDiscountChange = (value) => {
    setFormData(prev => ({ ...prev, discount: parseFloat(value) || 0 }));
    calculateTotals(formData.line_items, formData.tax_rate, value);
  };

  const handleKeyDown = (e) => {
    // Prevent Enter key from submitting the form
    // except when the submit button is focused
    if (e.key === 'Enter' && e.target.type !== 'textarea') {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.customer_id) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please select a customer"
      });
      return;
    }
    if (formData.line_items.length === 0 || !formData.line_items[0].description) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please add at least one line item"
      });
      return;
    }

    try {
      setSaving(true);
      
      // Prepare data for API
      const invoiceData = {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString()
      };

      if (isEdit) {
        await invoiceService.update(invoiceId, invoiceData);
      } else {
        await invoiceService.create(invoiceData);
      }
      
      navigate('/invoices');
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: 'Failed to save invoice: ' + (error.message || 'Unknown error')
      });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/invoices')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Invoices
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Invoice' : 'Create New Invoice'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Customer Information</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="customer">Select Customer *</Label>
                  <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={customerSearchOpen}
                        className="w-full justify-between"
                      >
                        {formData.customer_id
                          ? (() => {
                              const customer = selectedCustomer;
                              if (customer) {
                                const firstName = customer.first_name || customer.name?.split(' ')[0] || '';
                                const lastName = customer.last_name || customer.name?.split(' ').slice(1).join(' ') || '';
                                const displayName = firstName && lastName ? `${firstName} ${lastName}` : customer.name || 'Unknown';
                                return `${displayName} (${customer.email})`;
                              }
                              // Edit mode: customer fields come from the saved invoice
                              const saved = `${formData.customer_first_name || ''} ${formData.customer_last_name || ''}`.trim();
                              return saved || "Choose a customer...";
                            })()
                          : "Search and select a customer..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[600px] p-0">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Search customers by name or email..."
                          value={customerSearch}
                          onValueChange={setCustomerSearch}
                        />
                        <CommandEmpty>No customer found.</CommandEmpty>
                        <CommandGroup className="max-h-[300px] overflow-y-auto">
                          {customers.map((customer) => {
                            const firstName = customer.first_name || customer.name?.split(' ')[0] || '';
                            const lastName = customer.last_name || customer.name?.split(' ').slice(1).join(' ') || '';
                            const displayName = firstName && lastName ? `${firstName} ${lastName}` : customer.name || 'Unknown';
                            const searchText = `${displayName} ${customer.email} ${customer.phone || ''}`.toLowerCase();
                            
                            return (
                              <CommandItem
                                key={customer.id}
                                value={searchText}
                                onSelect={() => {
                                  handleCustomerChange(customer.id);
                                  setCustomerSearchOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    formData.customer_id === customer.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{displayName}</span>
                                  <span className="text-sm text-gray-500">{customer.email}</span>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {formData.customer_id && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Name:</span> {formData.customer_first_name} {formData.customer_last_name}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Email:</span> {formData.customer_email}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Phone:</span> {formData.customer_phone}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Address:</span> {formData.customer_address}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Invoice Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="invoice_date">Invoice Date *</Label>
                  <Input
                    type="date"
                    id="invoice_date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="due_date">Due Date *</Label>
                  <Input
                    type="date"
                    id="due_date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Select
                    value={formData.payment_terms}
                    onValueChange={(value) => {
                      setFormData(prev => ({ 
                        ...prev, 
                        payment_terms: value,
                        terms_conditions: getTermsForPaymentTerms(value)
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map((term) => (
                        <SelectItem key={term} value={term}>
                          {term}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Line Items</h2>
                <Button type="button" onClick={addLineItem} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>

              <div className="space-y-4">
                {formData.line_items.map((item, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-5">
                        <Label>Description *</Label>
                        <Input
                          list={`service-items-${index}`}
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          placeholder="Type or select a service..."
                        />
                        <datalist id={`service-items-${index}`}>
                          {SERVICE_ITEMS.map((service) => (
                            <option key={service} value={service} />
                          ))}
                        </datalist>
                      </div>
                      <div className="col-span-2">
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Price</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => handleLineItemChange(index, 'price', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Total</Label>
                        <Input
                          type="text"
                          value={formatCurrency(item.total)}
                          disabled
                          className="bg-gray-50"
                        />
                      </div>
                      <div className="col-span-1 flex items-end">
                        {formData.line_items.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Additional Information</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="notes">Notes (Internal)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    placeholder="Internal notes (not visible on invoice)"
                  />
                </div>
                <div>
                  <Label htmlFor="terms_conditions">Terms & Conditions</Label>
                  <Textarea
                    id="terms_conditions"
                    value={formData.terms_conditions}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms_conditions: e.target.value }))}
                    rows={4}
                    placeholder="Terms and conditions for this invoice"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <h2 className="text-lg font-semibold mb-4">Invoice Summary</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(formData.subtotal)}</span>
                </div>
                
                <div className="border-t pt-3">
                  <Label htmlFor="tax_rate" className="text-sm">Tax Rate (%)</Label>
                  <Input
                    type="number"
                    id="tax_rate"
                    min="0"
                    step="0.1"
                    value={formData.tax_rate}
                    onChange={(e) => handleTaxRateChange(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax Amount</span>
                  <span className="font-medium">{formatCurrency(formData.tax_amount)}</span>
                </div>
                
                <div className="border-t pt-3">
                  <Label htmlFor="discount" className="text-sm">Discount ($)</Label>
                  <Input
                    type="number"
                    id="discount"
                    min="0"
                    step="0.01"
                    value={formData.discount}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div className="border-t pt-3 flex justify-between">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-lg font-bold text-brand-600">
                    {formatCurrency(formData.total)}
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                <Button
                  type="submit"
                  className="w-full bg-brand-600 hover:bg-brand-700"
                  disabled={saving}
                >
                  {saving ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isEdit ? 'Update Invoice' : 'Create Invoice'}
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/invoices')}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>

              <div className="mt-4">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}